import { ComponentProps } from 'preact';
import { useMemo } from 'preact/hooks';
import { Label } from '../../UNSAFE_Label';
import { TextField } from '../../UNSAFE_TextField';
import { InlineUserAssistance } from '../../UNSAFE_UserAssistance';
import { FormFieldContextProps } from '../UNSAFE_useFormFieldContext';
import { useId } from '../UNSAFE_useId';

type PickedUserAssistanceProps = Pick<ComponentProps<typeof InlineUserAssistance>, 'messages'>;

type UseTextFieldProps<V> = PickedUserAssistanceProps & {
  ariaDescribedBy?: string;
  contentVariant?: 'input' | 'textarea';
  isDisabled?: boolean;
  isFocused?: boolean;
  isLoading?: boolean;
  isReadonly?: boolean;
  labelEdge?: ComponentProps<typeof TextField>['labelEdge'] | 'none';
  styleVariant?: 'default' | 'embedded';
  value?: V;
};

const rankedSeverities = ['error', 'warning', 'confirmation', 'info', 'none'] as const;

/**
 * Merge multiple ids into a space-separated list
 * @param ids The ids to be merged
 * @returns The space separated string of ids
 */
function mergeIDs(...ids: (string | undefined)[]) {
  return ids.filter(Boolean).join(' ') || undefined;
}

/**
 * A custom hook to determine the props for a component that renders
 * a text field
 */
export function useTextField<V>({
  ariaDescribedBy,
  contentVariant,
  isDisabled,
  isFocused,
  isLoading,
  isReadonly,
  labelEdge,
  messages,
  styleVariant,
  value
}: UseTextFieldProps<V>) {
  const baseId = useId();
  const labelId = labelEdge !== 'none' ? `${baseId}-label` : undefined;
  const inputId = !isReadonly ? `${baseId}-input` : undefined;
  const uaId = !(isDisabled || isReadonly) ? `${baseId}-ua` : undefined;

  // Create the form field context
  const formFieldContext = useMemo<FormFieldContextProps>(() => {
    const hasValue = value !== undefined && (typeof value !== 'string' || value !== '');
    return isReadonly
      ? {
          hasValue: hasValue,
          isFocused: isFocused,
          isLoading,
          isReadonly: true
        }
      : isDisabled
      ? {
          hasValue: hasValue,
          isDisabled: true,
          isLoading
        }
      : {
          hasValue: hasValue,
          isFocused: isFocused,
          isLoading
        };
  }, [isDisabled, isFocused, isLoading, isReadonly, value]);

  // Determine the highest severity from the messages
  const messageSeverity = useMemo(() => {
    return messages === undefined
      ? undefined
      : messages.reduce<typeof rankedSeverities[number]>((accSeverity, currMessage) => {
          const currSeverity = currMessage.severity || 'error';
          return rankedSeverities.indexOf(accSeverity) < rankedSeverities.indexOf(currSeverity)
            ? accSeverity
            : currSeverity;
        }, 'none');
  }, [messages]);

  // Determine the label variant
  const labelVariant: ComponentProps<typeof Label>['variant'] =
    labelEdge === 'inside'
      ? messageSeverity === 'error'
        ? 'insideError'
        : messageSeverity === 'warning'
        ? 'insideWarning'
        : labelEdge
      : labelEdge !== 'none'
      ? labelEdge
      : undefined;

  return {
    baseId,
    formFieldContext,
    inputProps: {
      id: inputId,
      ariaDescribedBy: mergeIDs(uaId, ariaDescribedBy),
      ariaInvalid: messageSeverity === 'error' ? 'true' : undefined,
      variant: styleVariant
    },
    labelProps: {
      forId: inputId,
      id: labelId,
      variant: labelVariant
    },
    textFieldProps: {
      contentVariant,
      statusVariant: /error|warning/.test(messageSeverity ?? '')
        ? (messageSeverity as 'error' | 'warning')
        : undefined,
      styleVariant
    },
    userAssistanceProps: {
      id: uaId
    }
  };
}
